import type { ApplicationListItem } from '@like-aiop/backend/src/api/applications/types';
import { ensureResponseOk } from '../../../shared/errors/HttpError';
import type { ApiClient } from '../../auth/api/clients';
import type { Honbu } from '../types';

// クライアント×本部単位の単一フライトキャッシュ。同一本部の再取得を防ぎつつ、
// 引数で受けたクライアントに依存する結果をモジュール全体で共有しない（テスト間の持ち越し防止）。
const applicationsPromises = new WeakMap<ApiClient, Map<Honbu, Promise<ApplicationListItem[]>>>();

const fetchApplications = async (apiClient: ApiClient, honbu: Honbu): Promise<ApplicationListItem[]> => {
  const res = await apiClient.applications.$get({ query: { honbu } });
  // guardedFetch はリフレッシュ失敗後の 401 をレスポンスとして返すため、ここで HttpError に変換する。
  await ensureResponseOk(res);
  const data = await res.json();

  return data.map(item => ({
    ...item,
    birthDate: item.birthDate ? new Date(item.birthDate) : null,
    calledAt: item.calledAt ? new Date(item.calledAt) : null,
    syncedToRpmAt: item.syncedToRpmAt ? new Date(item.syncedToRpmAt) : null,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  }));
};

/**
 * 指定した本部の架電履歴取得 Promise を、クライアント×本部単位で単一フライトキャッシュして返す。
 * 取得完了まで suspend するため Suspense 境界内で使うこと。
 * 失敗時はキャッシュを破棄し、再マウント時に再取得できるようにする。
 */
export const createApplicationsPromise = (apiClient: ApiClient, honbu: Honbu): Promise<ApplicationListItem[]> => {
  let byHonbu = applicationsPromises.get(apiClient);
  if (byHonbu === undefined) {
    byHonbu = new Map();
    applicationsPromises.set(apiClient, byHonbu);
  }

  const cached = byHonbu.get(honbu);
  if (cached !== undefined) return cached;

  const promise = fetchApplications(apiClient, honbu).catch((error: unknown) => {
    byHonbu.delete(honbu);
    throw error;
  });
  byHonbu.set(honbu, promise);
  return promise;
};
